import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The narration voice (ADR 0011), as a hook the play surface owns.
 *
 * One player, one voice at a time. A turn is addressed by its thread key —
 * never by its text — and the audio for it comes back through the one
 * function the caller hands in (packages/client's method, passed down).
 * Audio already fetched is kept in memory for this page so that pressing
 * listen twice does not pay twice; nothing is written anywhere.
 */
export type FetchAudioFn = (key: string) => Promise<Blob>;

export interface NarrationAudioControl {
  readonly playingKey: string | null;
  readonly loadingKey: string | null;
  readonly failedKey: string | null;
  /** Play this turn; pressing the turn that is already playing stops it. */
  readonly play: (key: string) => void;
  readonly stop: () => void;
}

export function useNarrationAudio(fetchAudio: FetchAudioFn | null): NarrationAudioControl {
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const player = useRef<HTMLAudioElement | null>(null);
  const urls = useRef(new Map<string, string>());
  const requested = useRef<string | null>(null);
  const latestFetch = useRef(fetchAudio);
  latestFetch.current = fetchAudio;

  const stop = useCallback(() => {
    requested.current = null;
    const audio = player.current;
    if (audio !== null) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlayingKey(null);
    setLoadingKey(null);
  }, []);

  const play = useCallback(
    (key: string) => {
      const fetcher = latestFetch.current;
      if (fetcher === null) return;
      if (requested.current === key) {
        stop();
        return;
      }
      stop();
      requested.current = key;
      setFailedKey(null);

      const cached = urls.current.get(key);
      const ready = cached !== undefined ? Promise.resolve(cached) : fetcher(key).then((blob) => {
        const url = URL.createObjectURL(blob);
        urls.current.set(key, url);
        return url;
      });
      if (cached === undefined) setLoadingKey(key);

      ready
        .then(async (url) => {
          if (requested.current !== key) return;
          player.current ??= new Audio();
          const audio = player.current;
          audio.onended = () => {
            if (requested.current === key) {
              requested.current = null;
              setPlayingKey(null);
            }
          };
          audio.src = url;
          await audio.play();
          if (requested.current !== key) return;
          setLoadingKey(null);
          setPlayingKey(key);
        })
        .catch(() => {
          if (requested.current !== key) return;
          requested.current = null;
          setLoadingKey(null);
          setPlayingKey(null);
          setFailedKey(key);
        });
    },
    [stop],
  );

  useEffect(() => {
    const held = urls.current;
    return () => {
      player.current?.pause();
      for (const url of held.values()) URL.revokeObjectURL(url);
      held.clear();
    };
  }, []);

  return { playingKey, loadingKey, failedKey, play, stop };
}
